import { Directive, HostBinding } from "@angular/core";
import { Lang, LangSection, LangText } from "../../types/language";
import * as english from './en.json';
import * as french from './fr.json';

@Directive()
export abstract class LocalizableComponent {
  langSection = LangSection;
  langText = LangText;

  @HostBinding('class.french') get isFrench() {
    return this.getLanguage() === Lang.French;
  }

  private languageCode: Lang;
  private languageModel: any;

  constructor() {
    this.languageCode = localStorage.getItem('lang') as Lang | undefined ||
      navigator.languages.find(x => x.indexOf('-') === -1 && (x == 'fr' || x == 'en')) as Lang | undefined ||
      Lang.English;

    this.languageModel = ((
      this.languageCode === Lang.French ? french
        : english) as any).default;
  }

  /**
   * Used to retrieve the correct language variant text
   */
  localize(section: LangSection, key: LangText) {
    if (!this.languageModel[section] || !this.languageModel[section][key]) {
      //console.log(`Localization text not found: ${section}.${key}`);
      return localStorage.getItem('show_lang_errors') ? `FIX_ME (${section}.${key})` : '';
    }
    return this.languageModel[section][key];
  }

  /**
   * Used to convert content that is already in English to the correct language variant
   * @example Text coming back from the API
   */
  localizeValue(section: LangSection, value: string) {
    let lowerValue = value?.toLowerCase() ?? '';
    const key = Object.keys(this.languageModel[section]).find(key => ((english as any).default[section][key])?.toLowerCase() === lowerValue);
    if (!key) {
      //console.log(`No conversion value found for ${section}.${key}`);
      return value;
    }

    return this.languageModel[section][key];
  }

  private get statusKeys(): Set<string> {
    return new Set(Object.keys((english as any).default[LangSection.StatusValues] || {}));
  }

  /**
   * Translates a search query for language-isolated status matching.
   * When French: French status terms → English for API; English status terms → French to prevent cross-language matching.
   * Non-status terms always pass through unchanged.
   */
  protected translateSearchQueryToEnglish(section: LangSection, query: string): string | null {
    if (!query || this.languageCode === Lang.English) {
      return query;
    }

    const normalizedQuery = this.removeAccents(query.toLowerCase());
    const sectionData = this.languageModel[section];
    const englishSection = (english as any).default[section];
    if (!sectionData || !englishSection) {
      return query;
    }

    // Step 1: Try matching against French (localized) status values → return English equivalent
    const statusKeys = this.statusKeys;
    let bestFrenchMatch: { key: string; score: number } | null = null;

    for (const key of Object.keys(sectionData)) {
      if (!statusKeys.has(key) || typeof sectionData[key] !== 'string' || !englishSection[key]) continue;

      const normalizedLocal = this.removeAccents(sectionData[key].toLowerCase());

      if (normalizedLocal === normalizedQuery) {
        return englishSection[key];
      }

      if (normalizedLocal.includes(normalizedQuery) && normalizedQuery.length >= 3) {
        const score = normalizedQuery.length / normalizedLocal.length;
        if (!bestFrenchMatch || score > bestFrenchMatch.score) {
          bestFrenchMatch = { key, score };
        }
      }
    }

    if (bestFrenchMatch) {
      return englishSection[bestFrenchMatch.key];
    }

    // Step 2: Query matches an English status term on the French page.
    // Return the French display value so the API receives French text, which
    // does not match the English values stored in the database → 0 results.
    let bestEnglishMatch: { key: string; score: number } | null = null;

    for (const key of statusKeys) {
      if (!englishSection[key]) continue;
      const normalizedEnglish = this.removeAccents(englishSection[key].toLowerCase());

      if (normalizedEnglish === normalizedQuery) {
        // Exact English match → return French equivalent (API won't match → 0 results)
        return sectionData[key] || null;
      }

      if (normalizedEnglish.includes(normalizedQuery) && normalizedQuery.length >= 3) {
        const score = normalizedQuery.length / normalizedEnglish.length;
        if (!bestEnglishMatch || score > bestEnglishMatch.score) {
          bestEnglishMatch = { key, score };
        }
      }
    }

    if (bestEnglishMatch) {
      // Partial English match → return French equivalent (API won't match → 0 results)
      return sectionData[bestEnglishMatch.key] || null;
    }

    // Step 3: Non-status term — pass through unchanged
    return query;
  }

  getLanguageCode() {
    return this.languageCode;
  }

  private removeAccents(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  private getLanguage(): Lang {
    return this.languageCode;
  }
}
